import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, MenuItem, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ApiClient from "util/ApiClient";
import SessionClient from "util/SessionClient";
import actions from "rdx/modules/platform/actions";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type FormDataT = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  language: string;
};

const initialFormData: FormDataT = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  language: "",
};

type StepFormPropsT = {
  formData: FormDataT;
  onChange: (field: keyof FormDataT, value: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  t: TranslateFnT;
};

const StepForm = ({ formData, onChange, onSubmit, submitting, t }: StepFormPropsT) => (
  <Box
    component="form"
    onSubmit={(e: React.FormEvent) => {
      e.preventDefault();
      onSubmit();
    }}
  >
    <Typography sx={styles.eyebrow}>{t("form.eyebrow")}</Typography>
    <Typography variant="h3" sx={styles.formHeading}>
      {t("form.heading")}
      <Box component="span" sx={styles.gradientText}>
        {t("form.headingGradient")}
      </Box>
    </Typography>
    <Typography sx={styles.formSubtext}>{t("form.subtext")}</Typography>

    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.fields.firstName")}
          value={formData.firstName}
          onChange={(e) => onChange("firstName", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.fields.lastName")}
          value={formData.lastName}
          onChange={(e) => onChange("lastName", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.fields.email")}
          type="email"
          value={formData.email}
          onChange={(e) => onChange("email", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.fields.phone")}
          value={formData.phone}
          onChange={(e) => onChange("phone", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        <TextField
          fullWidth
          required
          select
          label={t("form.fields.languagePreference")}
          value={formData.language}
          onChange={(e) => onChange("language", e.target.value)}
          sx={styles.languageSelect}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: styles.languageMenuPaper,
              },
            },
          }}
        >
          <MenuItem value="en">{t("form.languages.english")}</MenuItem>
          <MenuItem value="es">{t("form.languages.spanish")}</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ width: "100%" }}>
        <Button fullWidth variant="contained" type="submit" disabled={submitting} sx={styles.ctaButton}>
          {submitting ? t("form.submittingButton") : t("form.submitButton")}
        </Button>
      </Box>
    </Box>
  </Box>
);

type SuccessStatePropsT = {
  email: string;
  t: TranslateFnT;
};

const SuccessState = ({ email, t }: SuccessStatePropsT) => (
  <Box sx={{ textAlign: "center", py: 4 }}>
    <Box sx={styles.successIconWrapper}>
      <MarkEmailReadIcon sx={{ fontSize: 40, color: "#F8AD3E" }} />
    </Box>
    <CheckCircleIcon sx={{ color: "#F8AD3E", fontSize: 28, mb: 1.5 }} />
    <Typography variant="h4" sx={styles.successHeading}>
      {t("success.heading")}
      <Box component="span" sx={styles.gradientText}>
        {t("success.headingGradient")}
      </Box>
    </Typography>
    <Typography sx={styles.successEmailLabel}>{t("success.emailLabel")}</Typography>
    <Typography sx={styles.successEmailValue}>{email || t("success.emailFallback")}</Typography>
    <Typography sx={styles.successHelpText}>{t("success.helpText")}</Typography>
  </Box>
);

const AmbassadorSignupContent = () => {
  const { t } = useTranslation("static_ambassador_signup");
  const [formData, setFormData] = useState<FormDataT>(initialFormData);
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user: userId } = useParams<{ user?: string }>();
  const prefix = userId ? `/${userId}` : "";
  const dispatch = useDispatch();

  const handleChange = (field: keyof FormDataT, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (submitting) return;
    setSubmitting(true);
    const session = new SessionClient();
    session.clear();
    const api = new ApiClient();
    const params = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      locale: formData.language,
      spoken_languages: formData.language ? [formData.language] : [],
      sponsor: userId || "powur.pbc",
      auth_format: "password",
      kind: "ambassador",
    };
    api
      .post("/registrations", params)
      .then(() => {
        setComplete(true);
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((res: any) => {
        if (
          res?.error?.message === "An account with this email address already exists" &&
          res?.error?.field === "email"
        ) {
          dispatch(
            actions.setAlertMessageVisible({
              message: t("errors.emailAlreadyExists"),
              severity: "info",
            }),
          );
        } else {
          dispatch(
            actions.setAlertMessageVisible({
              message: res?.error?.message || t("errors.genericError"),
              severity: "error",
            }),
          );
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.backgroundGlow} />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, py: { xs: 4, md: 6 } }}>
        <Box sx={styles.formCard}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "55%",
              height: "55%",
              background: "radial-gradient(ellipse at top left, rgba(248,173,62,0.14) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "50%",
              height: "50%",
              background: "radial-gradient(ellipse at bottom right, rgba(249,115,22,0.10) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          {complete ? (
            <Box key="success" sx={{ animation: "fadeUp 0.5s ease both" }}>
              <SuccessState email={formData.email} t={t} />
            </Box>
          ) : (
            <Box key="form">
              <StepForm
                formData={formData}
                onChange={handleChange}
                onSubmit={handleSubmit}
                submitting={submitting}
                t={t}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button component={Link} to={`${prefix}/ambassador`} startIcon={<ArrowBackIcon />} sx={styles.backButton}>
            {t("nav.backToAmbassador")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

const AmbassadorSignupPage = () => {
  const { t } = useTranslation("static_ambassador_signup");
  return (
    <StaticPageLayout title={t("meta.title")}>
      <AmbassadorSignupContent />
    </StaticPageLayout>
  );
};

export default AmbassadorSignupPage;
